import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '@ui-kit';
import { roleCreateSchema } from '../schema/roleSchema';
import type { RoleCreateFormValues } from '../schema/roleSchema';

interface RoleCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: RoleCreateFormValues) => void;
  isLoading?: boolean;
  errorMessage?: string;
}

export function RoleCreateDialog({
  open,
  onClose,
  onSubmit,
  isLoading = false,
  errorMessage,
}: RoleCreateDialogProps) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<RoleCreateFormValues>({
    resolver: zodResolver(roleCreateSchema),
    defaultValues: {
      priority: 100,
    },
  });

  // 닫힐 때 폼 리셋
  useEffect(() => {
    if (!open) {
      reset({ priority: 100 });
    }
  }, [open, reset]);

  // Esc 키 닫기
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  const handleFormSubmit = handleSubmit((values) => {
    onSubmit(values);
  });

  const fieldStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--spacing-1)',
    marginBottom: 'var(--spacing-3)',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    color: 'var(--color-text-secondary)',
  };

  const inputStyle: React.CSSProperties = {
    padding: 'var(--spacing-2) var(--spacing-3)',
    background: 'var(--color-surface-elevated)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-sans)',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  };

  const errorStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-danger)',
    marginTop: 'var(--spacing-1)',
  };

  const hintStyle: React.CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    marginTop: 'var(--spacing-1)',
  };

  return (
    <FormDialog
      open={open}
      title={t('role.form.title.create')}
      onClose={onClose}
      onSubmit={() => void handleFormSubmit()}
      submitLabel={isLoading ? t('role.form.button.loading') : t('role.form.button.submit')}
      cancelLabel={t('role.form.button.cancel')}
    >
      <div>
        {/* 에러 배너 */}
        {errorMessage && (
          <div
            role="alert"
            style={{
              marginBottom: 'var(--spacing-3)',
              padding: 'var(--spacing-3)',
              background: 'color-mix(in srgb, var(--color-danger) 12%, var(--color-surface-muted))',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-danger)',
            }}
          >
            {errorMessage}
          </div>
        )}

        {/* 역할 코드 */}
        <div style={fieldStyle}>
          <label htmlFor="create-role-code" style={labelStyle}>
            {t('role.form.field.code')} *
          </label>
          <input
            id="create-role-code"
            type="text"
            placeholder={t('role.form.field.code.placeholder')}
            {...register('code')}
            aria-invalid={!!errors.code}
            aria-describedby={errors.code ? 'create-code-error' : 'create-code-hint'}
            style={{
              ...inputStyle,
              fontFamily: 'var(--font-family-mono)',
              borderColor: errors.code ? 'var(--color-danger)' : 'var(--color-surface-border)',
            }}
          />
          {errors.code ? (
            <span id="create-code-error" style={errorStyle} role="alert">
              {errors.code.message}
            </span>
          ) : (
            <span id="create-code-hint" style={hintStyle}>
              {t('role.form.field.code.hint')}
            </span>
          )}
        </div>

        {/* 한글 이름 */}
        <div style={fieldStyle}>
          <label htmlFor="create-role-nameKo" style={labelStyle}>
            {t('role.form.field.nameKo')} *
          </label>
          <input
            id="create-role-nameKo"
            type="text"
            placeholder={t('role.form.field.nameKo.placeholder')}
            {...register('nameKo')}
            aria-invalid={!!errors.nameKo}
            aria-describedby={errors.nameKo ? 'create-nameKo-error' : undefined}
            style={{
              ...inputStyle,
              borderColor: errors.nameKo ? 'var(--color-danger)' : 'var(--color-surface-border)',
            }}
          />
          {errors.nameKo && (
            <span id="create-nameKo-error" style={errorStyle} role="alert">
              {errors.nameKo.message}
            </span>
          )}
        </div>

        {/* 영문 이름 */}
        <div style={fieldStyle}>
          <label htmlFor="create-role-nameEn" style={labelStyle}>
            {t('role.form.field.nameEn')}
          </label>
          <input
            id="create-role-nameEn"
            type="text"
            placeholder={t('role.form.field.nameEn.placeholder')}
            {...register('nameEn')}
            aria-invalid={!!errors.nameEn}
            style={{
              ...inputStyle,
              borderColor: errors.nameEn ? 'var(--color-danger)' : 'var(--color-surface-border)',
            }}
          />
        </div>

        {/* 설명 */}
        <div style={fieldStyle}>
          <label htmlFor="create-role-description" style={labelStyle}>
            {t('role.form.field.description')}
          </label>
          <textarea
            id="create-role-description"
            placeholder={t('role.form.field.description.placeholder')}
            rows={2}
            {...register('description')}
            style={{
              ...inputStyle,
              resize: 'vertical',
              minHeight: 60,
            }}
          />
        </div>

        {/* 우선순위 */}
        <div style={fieldStyle}>
          <label htmlFor="create-role-priority" style={labelStyle}>
            {t('role.form.field.priority')}
          </label>
          <input
            id="create-role-priority"
            type="number"
            placeholder={t('role.form.field.priority.placeholder')}
            min={1}
            max={999}
            {...register('priority', { valueAsNumber: true })}
            aria-invalid={!!errors.priority}
            aria-describedby={errors.priority ? 'create-priority-error' : 'create-priority-hint'}
            style={{
              ...inputStyle,
              borderColor: errors.priority ? 'var(--color-danger)' : 'var(--color-surface-border)',
            }}
          />
          {errors.priority ? (
            <span id="create-priority-error" style={errorStyle} role="alert">
              {errors.priority.message}
            </span>
          ) : (
            <span id="create-priority-hint" style={hintStyle}>
              {t('role.form.field.priority.hint')}
            </span>
          )}
        </div>
      </div>
    </FormDialog>
  );
}
